import React, { useEffect, useRef } from 'react';

interface BackgroundSoundProps {
  theme?: string;
  isPlaying: boolean;
  volume: number;
}

/**
 * BackgroundSound component provides brown noise regardless of the theme setting
 */
const BackgroundSound: React.FC<BackgroundSoundProps> = ({ isPlaying, volume = 0.1 }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  
  // Create and initialize audio element
  useEffect(() => {
    // Always use brown noise regardless of theme
    const soundUrl = '/sounds/brown-noise.mp3';
    
    console.log(`Initializing brown noise background sound`);
    
    // Make sure we have the full URL path
    const baseUrl = process.env.PUBLIC_URL || window.location.origin;
    const fullSoundUrl = new URL(soundUrl, baseUrl).toString();
    
    console.log(`Sound URL: ${fullSoundUrl}`);
    
    // Create new audio element if it doesn't exist
    if (!audioRef.current) {
      const audio = new Audio();
      audio.loop = true;
      
      // Add error listeners for debugging
      audio.addEventListener('error', (e) => {
        console.error('Background audio error:', e);
        console.error('Audio error code:', (e.target as HTMLMediaElement).error?.code);
        console.error('Audio error message:', (e.target as HTMLMediaElement).error?.message);
      });
      
      // Add loaded data listener
      audio.addEventListener('loadeddata', () => {
        console.log('Background audio loaded successfully');
      });
      
      audioRef.current = audio;
    }
    
    // Set the source and load the audio
    try {
      audioRef.current.src = fullSoundUrl;
      audioRef.current.load();
      console.log('Background audio loading started');
    } catch (err) {
      console.error('Error loading background audio:', err);
    }
    
    // Cleanup function
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, []); // Only run once on mount
  
  // Handle playback and volume changes
  useEffect(() => {
    if (!audioRef.current) return;
    
    // Set volume
    audioRef.current.volume = volume;
    console.log(`Setting background volume to ${volume}`);
    
    // Control playback
    if (isPlaying && volume > 0) {
      console.log('Attempting to play background audio');
      audioRef.current.play()
        .then(() => console.log('Background audio playing'))
        .catch(err => {
          console.warn('Background audio playback prevented:', err);
          
          // If autoplay is blocked, we can try again on user interaction
          const retryPlay = () => {
            if (audioRef.current && isPlaying) {
              audioRef.current.play()
                .then(() => {
                  console.log('Background audio playing after user interaction');
                  document.removeEventListener('click', retryPlay);
                })
                .catch(err => console.error('Still cannot play audio:', err));
            }
          };
          
          document.addEventListener('click', retryPlay, { once: true });
        });
    } else {
      console.log('Pausing background audio');
      audioRef.current.pause();
    }
    
    // Cleanup
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, [isPlaying, volume]);
  
  // This component doesn't render anything visible
  return null;
};

export default BackgroundSound; 